<template>
  <div class="member-list-person" :class="personClassesRef">
    <span v-if="showSuggestedParentRef" class="suggested-parent-icon">*</span>
    <ExpandableItem 
      v-if="listPerson.potentialMembers.length != 1"
      :checkbox="sharingMode"
      :checked="personSelectStateRef === true"
      :indeterminate="personSelectStateRef === undefined"
      :checkDisabled="!canSelectPersonRef"
      class="multiple-profiles"
      @check="onPersonCheck">
      <template #item>
        <div class="view-person">
          <img v-if="hasSharedProfilesRef" src="@/assets/icons/person_gray_150.png">
          <img v-else src="@/assets/icons/person_dotted_80.png">
          {{ listPerson.displayName }}
          <span class="count">({{ listPerson.potentialMembers.length }} profiles)</span>
        </div>
      </template>
      <template #content>
        <ul class="list-unstyled member-list">
          <li v-for="pm in listPerson.potentialMembers" :key="pm.id">
            <FamilyGroupPotentialMember
              :pm="pm"
              :allow-add="allowAdd"
              :sharing-mode="sharingMode"
              @invite="emit('memberInvite', pm)"
              @click="emit('memberClick', pm)"/>
          </li>
        </ul>
      </template>
    </ExpandableItem>
    <template v-else>
      <FamilyGroupPotentialMember 
        :pm="listPerson.potentialMembers[0]"
        :allow-add="allowAdd"
        :sharing-mode="sharingMode"
        @invite="emit('memberInvite', listPerson.potentialMembers[0])"
        @click="emit('memberClick', listPerson.potentialMembers[0])"/>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.member-list-person {
  position: relative;
  display: flex;
  align-items: center;

  .suggested-parent-icon {
    position: absolute;
    top: 3px;
    left: -1rem;
    color: red;
  }

  & > .expandable-item {
    margin-left: -20px;
  }

  .view-person {
    display: flex;
    align-items: center;
    color: #aaa;

    img {
      margin-right: 6px;
      width: 18px;
      opacity: 80%; // not shared or member
    }

    .count {
      margin-left: 0.5rem;
      font-size: 0.875rem;
    }
  }

  &.member, &.shared {
    .view-person {
      color: black;

      .count {
        color: #888;
      }
    }
  }

  &.member .view-person img {
    opacity: 60%;
  }

  &.shared .view-person img {
    opacity: 30%;
  }

  .member-list {
    margin-left: 24px;

    li {
      display: flex;
      align-items: center;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, inject } from 'vue'
import { PotentialMember } from './FamilyGroupMemberManager'
import { ListPerson } from './FamilyGroupMemberListPerson'
import { ProfileSharingState } from './ProfileSharingState'
import { getMultiSelectState } from '@/util/InputUtil'
import ExpandableItem from '@/util/ExpandableItem.vue'
import FamilyGroupPotentialMember from './FamilyGroupPotentialMember.vue'

const props = defineProps<{
  familyGroupId: string
  listPerson: Readonly<ListPerson>
  allowAdd?: boolean
  sharingMode?: boolean
  suggestedParent?: boolean
}>()

const emit = defineEmits<{
  (e: 'memberClick', pm: PotentialMember): void
  (e: 'memberInvite', pm: PotentialMember): void
}>()

const sharingState = inject<ProfileSharingState>('profileSharingState')!

const hasSharedProfilesRef = computed(() => props.listPerson.potentialMembers.some(isMemberSelected))
const hasMembersRef = computed(() => props.listPerson.potentialMembers.some(pm => pm.member))
const personClassesRef = computed(() => {
  const classes: string[] = []
  if (hasMembersRef.value) {
    classes.push('member')
  }
  else if (hasSharedProfilesRef.value) {
    classes.push('shared')
  }
  return classes
})
const personSelectStateRef = computed(() => getMultiSelectState(props.listPerson.potentialMembers, isMemberSelected))
const canSelectPersonRef = computed(() => props.listPerson.potentialMembers.some(canSelectMember))
const showSuggestedParentRef = computed(() => props.allowAdd && props.sharingMode && props.suggestedParent && 
  !props.listPerson.potentialMembers.some(pm => sharingState.isShared(pm.profileGroupId)))

function isMemberSelected(pm: PotentialMember) {
    return sharingState.isShared(pm.profileGroupId)
}

function canSelectMember(pm: PotentialMember) {
  return pm.isProfileOwner
}

function onPersonCheck() {
  const selectableMembers = props.listPerson.potentialMembers.filter(canSelectMember)
  const oldState = getMultiSelectState(selectableMembers, isMemberSelected)
  sharingState.shareProfiles(selectableMembers.map(pm => pm.profileGroupId!), !oldState) // none/some -> all, all -> none
}
</script>
